import { IContextualMenuItem } from '@fluentui/react'
import useAxios from '../../../hooks/useAxios'
import { InvoiceStatus, InvoiceType } from '../../../config/finance/invoiceConfig'
import { accountingToastStyle, submitAccountingInvoice } from '../../../components/Preview/AccountingSubmissionPreview'
import { OverviewItem } from '../../../components/List/Invoice/InvoiceList'
import { stealthClick } from '../../../components/StealthLink'
import { useQueue } from '../../useQueue'
import { DocumentGenerationStatusData, DocumentGenerationType } from '../../document/useDocumentGenerationHandler'
import useSubmitDocumentGenerationRequest from '../report/useSubmitDocumentGenerationRequest'
import useComposeInvoiceOutlookMessage from './useComposeInvoiceOutlookMessage'
import { getDriveItemDownload } from '../../../helpers/documentHelper'
import { QueueStatus } from '../../useQueueStateHandler'
import { useTranslation } from 'react-i18next'

export type InvoiceParams = {
    invoiceId: string
}

export default function useInvoiceListMenuItems(reset: () => void): IContextualMenuItem[][] {
    const axios = useAxios()
    const composeOutlookMessage = useComposeInvoiceOutlookMessage()
    const toastStyles = accountingToastStyle()
    const { t: translate } = useTranslation()
    const queue = useQueue()

    const generatePdf = useSubmitDocumentGenerationRequest<InvoiceParams, DocumentGenerationStatusData>(
        DocumentGenerationType.Invoice,
    )

    return [
        [
            {
                key: 'download-pdf',
                iconProps: { iconName: 'Download' },
                text: translate('action.download'),
                access: (item: OverviewItem) => !queue.has(item.id) && item.pdfId !== null,
                filter: (item: OverviewItem) => ![InvoiceType.Split].includes(item.type),
                click: (item: OverviewItem) => stealthClick({ id: item.pdfId }, axios),
            },
            {
                key: 'create-pdf',
                iconProps: { iconName: 'Refresh' },
                text: translate('action.generate'),
                access: (item: OverviewItem) => !queue.has(item.id) && item.pdfId === null,
                filter: (item: OverviewItem) => ![InvoiceType.Split].includes(item.type),
                click: (item: OverviewItem) =>
                    generatePdf({ invoiceId: item.id }, item.number, {
                        [QueueStatus.Finished]: data => {
                            reset()
                            stealthClick({ driveItem: data.status.driveItem }, axios)
                        },
                    }),
            },
            {
                key: 'send-pdf',
                iconProps: { iconName: 'MailForward' },
                text: translate('action.send'),
                access: (item: OverviewItem) => item.status === InvoiceStatus.Final && !queue.has(item.id),
                filter: (item: OverviewItem) => ![InvoiceType.Split].includes(item.type),
                click: async (item: OverviewItem) => {
                    if (item.pdfId === null) {
                        generatePdf({ invoiceId: item.id }, item.number, {
                            [QueueStatus.Finished]: data => {
                                reset()
                                composeOutlookMessage(item.id, item.recipient?.email || '', data.status.driveItem)
                            },
                        })
                    } else {
                        const pdf = await getDriveItemDownload(item.pdfId, axios)
                        await composeOutlookMessage(item.id, item.recipient?.email || '', pdf)
                    }
                },
            },
            {
                key: 'submit-accounting',
                iconProps: { iconName: 'ReportAdd' },
                text: translate('action.send.accounting'),
                access: (item: OverviewItem) =>
                    [InvoiceStatus.Final, InvoiceStatus.Settled, InvoiceStatus.Credited].includes(item.status) &&
                    item.unit &&
                    !item.transaction,
                filter: (item: OverviewItem) => ![InvoiceType.Split].includes(item.type),
                click: async (item: OverviewItem) => submitAccountingInvoice(item, toastStyles, axios, reset),
            },
        ],
    ]
}
